
import React, { useEffect } from 'react';
import { ICONS } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

interface ImageViewerProps {
  src: string | null;
  alt?: string;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  
  // New Action Props
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ 
  src, 
  alt, 
  onClose, 
  onNext, 
  onPrev, 
  hasNext, 
  hasPrev,
  isFavorite,
  onToggleFavorite,
  onDownload,
  onDelete
}) => {
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight' && onNext && hasNext) onNext();
        if (e.key === 'ArrowLeft' && onPrev && hasPrev) onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrev, hasNext, hasPrev]);

  if (!src) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-xl flex flex-col"
        onClick={onClose}
      >
        {/* Top Controls */}
        <div className="flex items-center justify-end p-6 absolute top-0 right-0 left-0 z-20 pointer-events-none">
          <button
            onClick={onClose}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors pointer-events-auto backdrop-blur-md border border-white/5"
          >
            <ICONS.Close className="w-6 h-6" />
          </button>
        </div>

        {/* Main Image Area */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-10 overflow-hidden relative group">
          
          {hasPrev && (
              <button 
                onClick={(e) => { e.stopPropagation(); onPrev && onPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-white/10 text-white rounded-full transition-colors z-20 md:opacity-0 md:group-hover:opacity-100 border border-white/5 backdrop-blur-md"
              >
                  <ICONS.ChevronLeft className="w-8 h-8" />
              </button>
          )}

          <motion.img
            key={src} // Force re-render on src change for animation
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            src={src}
            alt={alt || 'Full screen view'}
            className="max-w-full max-h-full object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()} 
          />

          {hasNext && (
              <button 
                onClick={(e) => { e.stopPropagation(); onNext && onNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-white/10 text-white rounded-full transition-colors z-20 md:opacity-0 md:group-hover:opacity-100 border border-white/5 backdrop-blur-md"
              >
                  <ICONS.ChevronRight className="w-8 h-8" />
              </button>
          )}
        </div>

        {/* Bottom Action Bar */}
        {(onToggleFavorite || onDownload || onDelete) && (
            <div className="absolute bottom-0 left-0 right-0 p-8 flex justify-center z-30 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none h-32 items-end">
                <div 
                    className="pointer-events-auto flex items-center gap-0 bg-black/60 backdrop-blur-xl border border-white/10 rounded-full px-2 py-1 shadow-2xl mb-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    {onToggleFavorite && (
                        <button 
                            onClick={onToggleFavorite}
                            className={clsx(
                                "p-3 rounded-full transition-all hover:bg-white/10 active:scale-95",
                                isFavorite ? "text-red-500" : "text-zinc-300 hover:text-white"
                            )}
                            title="Favorite"
                        >
                            <ICONS.Heart className={clsx("w-6 h-6", isFavorite && "fill-current")} />
                        </button>
                    )}
                    
                    {onToggleFavorite && onDownload && <div className="w-px h-6 bg-white/10 mx-1"></div>}

                    {onDownload && (
                        <button 
                            onClick={onDownload}
                            className="p-3 rounded-full text-zinc-300 hover:text-white hover:bg-white/10 transition-all active:scale-95"
                            title="Download"
                        >
                            <ICONS.Download className="w-6 h-6" />
                        </button>
                    )}

                    {(onToggleFavorite || onDownload) && onDelete && <div className="w-px h-6 bg-white/10 mx-1"></div>}

                    {onDelete && (
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            className="p-3 rounded-full text-zinc-300 hover:text-red-500 hover:bg-white/10 transition-all active:scale-95"
                            title="Delete"
                        >
                            <ICONS.Trash className="w-6 h-6" />
                        </button>
                    )}
                </div>
            </div>
        )}

      </motion.div>
    </AnimatePresence>
  );
};

export default ImageViewer;
